import { DatePipe } from '@angular/common';
import { Component, inject, input, OnInit, output, viewChildren } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatCardContent, MatCardModule } from '@angular/material/card';
import { DateAdapter, MatOptionModule } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatFormFieldModule, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { editorConfig } from 'src/app/models/editorconfig';
import { UserService } from 'src/app/services/user.service';

import { Audit } from '../../../models/audit/audit';
import { Auditor } from '../../../models/auditor';
import { Categories } from '../../../models/categories';
import { Recommendation } from '../../../models/recommendation/recommendation';
import { State } from '../../../models/states';
import { AccessService } from '../../../services/access.service';
import { AuditService } from '../../../services/audit.service';
import { RecommendationService } from '../../../services/recommendation.service';
import { showWarningSnackbars } from '../../../utils/warning-snackbar.util';
import { EditorComponent } from '../../editor/editor.component';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { ProlongationDecisionComponent } from '../../prolongation-decision/prolongation-decision.component';
import { SettleDialogComponent } from '../../settle-dialog/settle-dialog.component';

@Component({
  selector: 'app-recommendation-auditors-processing',
  templateUrl: './recommendation-auditors-processing.component.html',
  styleUrls: ['./recommendation-auditors-processing.component.scss'],
  imports: [
    EditorComponent,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatError,
    MatLabel,
    MatSelectModule,
    MatOptionModule,
    MatRadioModule,
    MatDatepickerModule,
    MatSuffix,
    MatDatepicker,
    MatButtonModule,
    MatIconModule,
    CKEditorModule,
    MatCardContent,
    MatButton,
    DatePipe
  ],
  providers: [DatePipe]
})
export class RecommendationAuditorsProcessingComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auditService = inject(AuditService);
  private recommendationService = inject(RecommendationService);
  private accessService = inject(AccessService);
  dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private _adapter = inject<DateAdapter<Date>>(DateAdapter);

  readonly recommendations = input.required<Recommendation>();
  readonly form = input.required<UntypedFormGroup>();
  readonly auditorEditable = input<boolean>();
  readonly putToPending = output();
  readonly finallySettle = output();
  userService = inject(UserService);
  editorConfig = {};
  editor = ClassicEditor;
  currentTab = 0;
  datePipe = new DatePipe('en_EU');
  userDominoName!: string;
  fileLinkId!: string;
  prolongation_text = '';
  categories: string[] = [];
  priorities = [1, 2, 3, 4, 5, 6];

  finalLists: string[] = [];
  finalSmallLists: string[] = [];

  error!: string;

  options!: UntypedFormControl;

  array_mod_actions!: string[];
  array_mod_dates!: string[];
  array_mod_statuses!: string[];

  isNewRecommendation = false;
  State = State;
  audit!: Audit;
  readonly fileUploader = viewChildren(FileUploadComponent);

  validation(): boolean {
    return this.form().valid;
  }

  ngOnInit() {
    //this.userDominoName = this.userService.userProfile().displayName;
    if (this.route.snapshot.params?.audit) {
      this.isNewRecommendation = true;
      if (window.history.state.audit) {
        this.audit = window.history.state.audit;
      } else {
        this.auditService.getDataById(this.route.snapshot.params.audit).subscribe({
          next: (auditEx) => (this.audit = auditEx)
        });
      }
    }
    switch (this.form()?.get('auditor')?.value) {
      case Auditor.GPIA:
        this.categories = Categories.GPIA;
        break;
      case Auditor.GPRM:
        this.categories = Categories.GPRM;
    }
    this.editorConfig = editorConfig.standard;
    this.currentTab = this.route.snapshot.params?.tab;
    this.dateFormat();
  }

  dateFormat() {
    this._adapter.setLocale('de');
  }

  adjournedChange() {
    this.form().controls['adjournedBy'].setValue(this.userDominoName);
    this.form().controls['adjournedReason'].setValidators([Validators.required]);
    this.form().controls['adjournedReason'].updateValueAndValidity();
  }

  // TODO reject reason only when not adjourned
  putBackToPending() {
    this.form().controls['adjournedReason'].setValidators(null);
    this.form().controls['adjournedReason'].updateValueAndValidity();
    const form = this.form();
    if (form.value.settledRejectReason || form.controls['status'].value === State.ADJOURNED) {
      form.enable();
      form.controls['status'].setValue('Pending');
      form.controls['amountCapex'].setValue('');
      const recommendations = this.recommendations();
      recommendations.status = 'Pending';
      recommendations.adjournedReason = '';
      recommendations.amountCapex = null;
      form.controls['shortText1'].setValue('');
      form.controls['settledBy'].setValue('');
      this.putToPending.emit();
    } else {
      this.snackBar.open("Fill in 'Settle Reject reason'", undefined, {
        panelClass: 'error',
        duration: 4000
      });
    }
  }

  finallySettleRecommendation() {
    this.form().enable();
    this.form().controls['status'].setValue('Finally settled');
    const recommendations = this.recommendations();
    recommendations.status = 'Finally settled';
    // SettledByFinally and SettledByFinallyName are automatically set by the backend
    this.finallySettle.emit();
  }

  enableEditMode(checked: boolean) {
    if (checked) {
      this.form().enable();
    } else {
      this.form().disable();
    }
  }

  isEditMode(): boolean {
    const form = this.form();
    if (form) {
      return form.enabled;
    } else {
      return false;
    }
  }

  isProlongationStatus(prolo_status: string) {
    return this.recommendations().prolongationStatus === prolo_status;
  }

  isOverdue(time: string): boolean {
    const today = new Date();
    const deadline = new Date(time);
    return deadline >= today;
  }

  radioButton(): boolean {
    return this.recommendations().isLegallyRequired;
  }

  isNew(): boolean {
    return this.isNewRecommendation;
  }

  isDefined(): boolean {
    if (this.recommendations()) {
      return true;
    } else {
      return false;
    }
  }

  isGPIA(): boolean {
    const recommendations = this.recommendations();
    if (recommendations) {
      return recommendations.auditDefinition.includes('GPIA');
    } else {
      return false;
    }
  }

  isStatus(status: string): boolean {
    const recommendations = this.recommendations();
    if (recommendations) {
      return recommendations.status === status;
    } else {
      return false;
    }
  }

  isAuditor(auditor: string) {
    return this.recommendations().auditor === auditor;
  }

  isDefinded() {
    return this.form();
  }

  mergeArray(
    arr1: string[],
    arr2: string[],
    arr3: string[],
    finalArray: { text: string; value1: string; value2: string }[]
  ) {
    arr1.forEach((element, i) => {
      finalArray.push({ text: element, value1: arr2[i], value2: arr3[i] });
    });
  }

  openDialog() {
    this.form().enable();
    this.dialog.open(SettleDialogComponent, {
      data: {
        formGroup: this.form(),
        recommendations: this.recommendations()
      }
    });
  }

  openDialogProlo() {
    this.form().enable();
    this.dialog.open(ProlongationDecisionComponent, {
      data: {
        formGroup: this.form(),
        recommendations: this.recommendations()
      }
    });
  }

  setProlongationText() {
    const recommendations = this.recommendations();
    if (recommendations.prolongationDecision === '1') {
      this.prolongation_text = 'The Prolongation request has been granted as required.';
    } else if (recommendations.prolongationDecision === '2') {
      this.prolongation_text = 'The new Deadline has been set to a Date defined by the Auditor.';
    } else if (recommendations.prolongationDecision === '0') {
      this.prolongation_text = 'The Prolongation request has been rejected!';
    } else {
      this.prolongation_text = '';
    }
  }

  setNewStatus(status: State) {
    this.form().controls['status'].setValue(status);
    this.recommendationService.setStatus(this.form().get('id')?.value, this.form().get('status')?.value).subscribe({
      next: (response) => {
        this.snackBar.open('Save successful!', undefined, { duration: 2000 });
        this.showEmailWarnings(response.emailWarnings);
        this.router.navigate(['/recommendations/', this.form().get('auditor')?.value]);
      },
      error: () => {
        this.snackBar.open('Something went wrong contact the IT!', undefined, {
          panelClass: 'error',
          duration: 2000
        });
      }
    });
  }

  clearNewDeadline() {
    this.form().controls['newDeadline'].setValue('');
  }

  private showEmailWarnings(emailWarnings?: string[]): void {
    showWarningSnackbars(this.snackBar, emailWarnings, 4000);
  }
}
